// components/ImageGeo/GeoMarker.jsx

import React, { useRef, useCallback, useEffect, useState } from "react";
import { CircleMarker, Tooltip } from "react-leaflet";
import L from "leaflet";
import Puce from "./Puce";
import { useDeviceDetection } from "../../hooks/useDeviceDetection";
import LoadingSquareAlea from "../Common/LoadingSquareAlea";

/* --------------------------------------------------
     Hook custom pour gérer les tooltips
  -------------------------------------------------- */
// Hook custom pour gérer les tooltips avec état de chargement
const useTooltipManager = (tooltipRef, thumbUrl) => {
  const closeTimeoutRef = useRef(null);
  const isPreloadedRef = useRef(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const preloadImage = useCallback(() => {
    if (isPreloadedRef.current) {
      // Si déjà préchargée, on met à jour l'état
      setImageLoaded(true);
      return;
    }

    // Crée une nouvelle image pour précharger
    const img = new Image();
    img.src = thumbUrl;
    
    img.onload = () => {
      isPreloadedRef.current = true;
      setImageLoaded(true);
    };
    
    img.onerror = () => {
      // En cas d'erreur, on considère comme chargé pour ne pas bloquer l'UI
      isPreloadedRef.current = true;
      setImageLoaded(true);
    };
  }, [thumbUrl]);

  const openTooltip = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    preloadImage();

    const tooltip = tooltipRef.current;
    if (!tooltip) return;

    const map = tooltip._map;
    if (!map) return;

    // Fermer tous les autres tooltips
    map.eachLayer((layer) => {
      if (layer instanceof L.Tooltip && layer !== tooltip && layer.isOpen()) {
        layer.close();
      }
    });

    tooltip.openOn(map);
  }, [tooltipRef, preloadImage]);

  const scheduleClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }

    closeTimeoutRef.current = setTimeout(() => {
      if (tooltipRef.current) {
        tooltipRef.current.close();
      }
      closeTimeoutRef.current = null;
    }, 150);
  }, [tooltipRef]);

  const cancelClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  return { openTooltip, scheduleClose, cancelClose, imageLoaded };
};

/* --------------------------------------------------
     COMPOSANT PRINCIPAL SIMPLIFIÉ
  -------------------------------------------------- */
// COMPOSANT PRINCIPAL SIMPLIFIÉ
const GeoMarker = ({ img, darkMode, onOpenFullImage }) => {
  const { isMobile } = useDeviceDetection();
  const { coords, nyme: domo, id } = img;
  const color = img.typoloColor || "#888";
  const strokeColor = darkMode ? "#fff" : "#ffffff";
  const colorB = img.typoloColorB || color;

  // URL de la miniature
  const thumbUrl = `${process.env.PUBLIC_URL}/images_compress/thumbs/${id}`;

  // Refs
  const tooltipRef = useRef(null);

  // Hook de gestion du tooltip avec état de chargement
  const { openTooltip, scheduleClose, cancelClose, imageLoaded } = useTooltipManager(
    tooltipRef,
    thumbUrl
  );

  const clickHandler = !isMobile ? () => onOpenFullImage?.(img) : openTooltip;

  return (
    <CircleMarker
      center={[coords.lat, coords.lng]}
      radius={6}
      eventHandlers={{
        mouseover: openTooltip,
        mouseout: scheduleClose,
        click: clickHandler,
      }}
      pathOptions={{
        color: strokeColor,
        weight: 1,
        fillColor: color,
        fillOpacity: 1,
        className: "geo-marker",
      }}
    >
      <Tooltip
        ref={tooltipRef}
        interactive
        direction="auto"
        offset={[0, 0]}
        opacity={1}
        className="my-custom-tooltip"
        eventHandlers={{
          mouseover: cancelClose,
          mouseout: scheduleClose,
        }}
      >
        <div className="geo-tooltip">
       
          <div className="geo-tooltip-image">
            {/* LoadingSquare s'affiche pendant le chargement */}
            {true && (//!imageLoaded
              <div className="loading-square-wrapper">
                <LoadingSquareAlea
                  color={color} 
                  isLoaded={true}//imageLoaded}
                />
              </div>
            )}
            
            {/* Image qui apparaît une fois chargée */}
            <img 
              src={thumbUrl} 
              alt={`Miniature ${domo}`} 
              loading="lazy"
              className={`geo-tooltip-thumb ${imageLoaded ? 'loaded' : 'loading'}`}
              style={{ 
                opacity: imageLoaded ? 1 : 0,
                transition: 'opacity 0.3s ease'
              }}
              // Backup onLoad au cas où le préchargement échoue
              onLoad={(e) => {
                if (!imageLoaded) {
                  e.target.style.opacity = 1;
                }
              }}
            />
          </div>
          <div className="geo-tooltip-content">
            <Puce color={color} radius={6} />
            <strong>{domo?.toUpperCase()}</strong>
            <Puce color={colorB} radius={6} />
          </div>
        </div>
      </Tooltip>
    </CircleMarker>
  );
};

// Optimisation: mémoïsation avec comparaison approfondie
const areEqual = (prevProps, nextProps) => {
  return (
    prevProps.img.id === nextProps.img.id &&
    prevProps.darkMode === nextProps.darkMode
  );
};

export default React.memo(GeoMarker, areEqual);

/*
      onClick={(e) => {
        // empêcher si clic sur overlay ou boutons
        if (
          e.target.closest(".overlay") ||
          e.target.closest(".image-action-btn") ||
          e.target.closest(".image-action-btn-left")
        ) {
          return;
        }
        onOpenFull?.(img);
      }}

*/
